import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { customerService } from '@/services/api/customer.service';
import type { Customer } from '@/types/customer';

const formSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Enter a valid email'),
  phone: z.string().optional(),
  notes: z.string().max(1000).optional(),
});

type FormData = z.infer<typeof formSchema>;

interface CustomerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer?: Customer;
  onSaved?: (customer: Customer) => void;
}

const EMPTY_DEFAULTS: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  notes: '',
};

function toDefaults(customer?: Customer): FormData {
  if (!customer) return EMPTY_DEFAULTS;
  return {
    firstName: customer.firstName,
    lastName: customer.lastName,
    email: customer.email,
    phone: customer.phone ?? '',
    notes: customer.notes ?? '',
  };
}

export function CustomerFormDialog({ open, onOpenChange, customer, onSaved }: CustomerFormDialogProps) {
  const { t } = useTranslation(['customers', 'common']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!customer;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: toDefaults(customer),
  });

  useEffect(() => {
    form.reset(toDefaults(customer));
  }, [customer, form, open]);

  const mutation = useMutation({
    mutationFn: (data: FormData) => {
      const payload = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        phone: data.phone?.trim() || undefined,
        notes: data.notes?.trim() || undefined,
      };
      if (customer) {
        return customerService.updateCustomer(customer.publicId ?? customer.id, payload);
      }
      return customerService.createCustomer(payload);
    },
    onSuccess: (saved) => {
      toast({ title: isEdit ? t('customers:toasts.updateSuccess') : t('customers:toasts.createSuccess') });
      if (customer) {
        queryClient.invalidateQueries({ queryKey: ['customer', String(customer.publicId ?? customer.id)] });
        queryClient.invalidateQueries({ queryKey: ['customer', customer.publicId ?? String(customer.id)] });
      }
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer-search'] });
      onSaved?.(saved);
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: isEdit ? t('customers:toasts.updateError') : t('customers:toasts.createError'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const title = isEdit
    ? t('customers:editDialog.title')
    : t('customers:createDialog.title');
  const description = isEdit
    ? t('customers:editDialog.description')
    : t('customers:createDialog.description');
  const submitLabel = isEdit
    ? t('common:actions.save')
    : t('customers:createDialog.submit');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit((data) => mutation.mutate(data))} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers:fields.firstName')}</FormLabel>
                    <FormControl>
                      <Input className="h-12" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers:fields.lastName')}</FormLabel>
                    <FormControl>
                      <Input className="h-12" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:fields.email')}</FormLabel>
                  <FormControl>
                    <Input type="email" className="h-12" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:fields.phone')}</FormLabel>
                  <FormControl>
                    <Input type="tel" className="h-12" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:fields.notes')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={4}
                      placeholder={t('customers:fields.notesPlaceholder') as string}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
                {submitLabel}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
